import {
  ACCESSIBILITY_DESCRIPTION_ROUTE,
  FeatureToggleEnum,
  GOODIE_ROUTE,
  INVOICE_PARTIALPAYMENT_OVERVIEW_ROUTE,
  NEW_CLIENT_ROUTE,
  PRODUCTS_ROUTE,
  PROFILE_NEWSLETTER_ROUTE,
  PROFILE_ROUTE,
  TARIFF_CHANGE_EMOBILITY_ROUTE,
  TARIFF_CHANGE_ROUTE,
  TERMS_OF_USE_ROUTE,
} from '@mwe/constants';
import {
  AccountLogic,
  EnvironmentService,
  LoadingService,
  NavigationLogic,
  PopupService,
  productStore,
  ProfileService,
} from '@mwe/services';
import { AfterViewInit, ChangeDetectionStrategy, Component, effect, inject, Injector, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EnvironmentCustomerType } from '@mwe/environment';
import { IPopupModelV2 } from '@mwe/models';
import { OrderProductsPopupComponent } from '../../order-products-popup/order-products-popup.component';
import { TariffChangeSelectionPopupComponent } from '../../tariff-change-selection-popup/tariff-change-selection-popup.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { LinkComponent } from '../../link/link.component';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { containsStromGasOrWaermeProducts, isProductCategoryEmobility, isProductInternet } from '@mwe/utils';
import { filter } from 'rxjs';

@Component({
  selector: 'mwe-footer',
  templateUrl: './footer.component.html',
  imports: [TranslateDirective, LinkComponent, RouterLink, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent implements OnInit, AfterViewInit {
  private readonly environmentService = inject(EnvironmentService);
  private readonly navigationLogic = inject(NavigationLogic);
  private readonly popupService = inject(PopupService);
  private readonly store = inject(productStore);
  private readonly profileService = inject(ProfileService);
  private readonly loadingService = inject(LoadingService);
  private readonly router = inject(Router);
  private readonly accountLogic = inject(AccountLogic);
  private readonly injector = inject(Injector);

  readonly currentYear = signal<number>(new Date().getFullYear());
  readonly addressProductsLoadState = toSignal(this.loadingService.getLatestMergedLoadingStates('addressProducts'));

  private readonly tariffChangeQueryParamName = 'tarifWechselnPopup';

  links = {
    b2c: {
      newClient: NEW_CLIENT_ROUTE,
      partialPaymentOverview: INVOICE_PARTIALPAYMENT_OVERVIEW_ROUTE,
      goodies: GOODIE_ROUTE,
      newsletter: `${PROFILE_ROUTE}/${PROFILE_NEWSLETTER_ROUTE}`,
      tariffChange: TARIFF_CHANGE_ROUTE,
    },
    b2b: {
      newClient: NEW_CLIENT_ROUTE,
      tariffChange: TARIFF_CHANGE_ROUTE,
      newsletter: `${PROFILE_ROUTE}/${PROFILE_NEWSLETTER_ROUTE}`,
      goodies: GOODIE_ROUTE,
    },
    termsOfUseRoute: `/${TERMS_OF_USE_ROUTE}`,
    accessibilityDeclaration: `/${ACCESSIBILITY_DESCRIPTION_ROUTE}`,
  };

  ngOnInit(): void {
    // Listen for NavigationEnd events - handles same-portal logged-out flow where
    // auth guard preserves the URL (including query param) and restores it after OAuth.
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
      if (e.urlAfterRedirects?.includes(this.tariffChangeQueryParamName)) {
        this.router.navigate([], {
          queryParams: { [this.tariffChangeQueryParamName]: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
        this.handleTariffChangeIntent();
      }
    });
  }

  ngAfterViewInit(): void {
    this.checkOrderProductPopup();
    // Handles cross-portal flow: switchPortal appends ?tarifWechselnPopup=true to the URL,
    // so on initial load of the other portal we detect the param here in location.search.
    if (location.search.includes(this.tariffChangeQueryParamName)) {
      const url = new URL(location.href);
      url.searchParams.delete(this.tariffChangeQueryParamName);
      history.replaceState(null, null, url);
      this.handleTariffChangeIntent();
    }
  }

  navigateToB2C(route: string): void {
    this.navigationLogic.navigateToB2C(route);
  }

  navigateToB2B(route: string): void {
    this.navigationLogic.navigateToB2B(route);
  }

  // Tariff Change Selection Popup - START
  tariffChange(type: EnvironmentCustomerType): void {
    if (type !== this.environmentService.getPortalCustomerType()) {
      // Pass query param to switchPortal so it survives in the URL on the other portal.
      this.navigationLogic.switchPortal(`?${this.tariffChangeQueryParamName}=true`);
      return;
    }

    if (this.accountLogic.isLoggedOut()) {
      // Navigate to a protected route with the query param. The auth guard will store
      // this URL (including query param), trigger OAuth login, and after redirect
      // GatewayAuthComponent will navigate back to this URL. The NavigationEnd listener
      // above will then detect the query param and trigger the popup.
      this.router.navigate([`/${PRODUCTS_ROUTE}`], { queryParams: { [this.tariffChangeQueryParamName]: 'true' } });
      return;
    }

    this.openTariffChangeSelectionPopup();
  }

  private openTariffChangeSelectionPopup(): void {
    const isEmobilityTariffChangeEnabled = this.profileService.isFeatureToggleEnabled(
      FeatureToggleEnum.CHARGING_CARD_TARIFF_CHANGE_ENABLED,
    );
    const products = this.store.products();
    const availableCategories = products.filter(product => !isProductInternet(product)).map(product => product.category);
    const hasStromGas = containsStromGasOrWaermeProducts(products);
    const hasEmobility = availableCategories.some(category => isProductCategoryEmobility(category));

    // multiple eligible products -> decide via popup
    if (hasStromGas && hasEmobility) {
      const model: IPopupModelV2 = {
        id: 'tariff-change-selection-footer',
        component: TariffChangeSelectionPopupComponent,
        componentData: { availableCategories },
        dismissable: true,
        titleKey: 'tariffChange.selectionPopup.title',
        modalSize: 'xl',
      };
      this.popupService.openV2(model);
      return;
    }

    // only emobility -> navigate to emobility-tariff-change
    if (hasEmobility && isEmobilityTariffChangeEnabled) {
      this.router.navigateByUrl(`/${TARIFF_CHANGE_EMOBILITY_ROUTE}`);
      return;
    }

    // no eligible products -> navigate to tariff change with empty state
    // only strom -> navigate to normal tariff-change
    // feature toggle is not enabled -> navigate to normal tariff-change
    this.router.navigateByUrl(`/${TARIFF_CHANGE_ROUTE}`);
  }

  private handleTariffChangeIntent(): void {
    if (this.accountLogic.isLoggedOut()) {
      // Not yet logged in (cross-portal initial load): navigate to a protected route
      // with the query param so the auth guard triggers OAuth and restores the URL after login.
      this.router.navigate([`/${PRODUCTS_ROUTE}`], { queryParams: { [this.tariffChangeQueryParamName]: 'true' } });
      return;
    }

    if (this.isTariffChangeDataReady()) {
      this.openTariffChangeSelectionPopup();
      return;
    }

    // Products not yet fully initialized after login — wait reactively for final product data.
    let handled = false;
    effect(
      () => {
        if (this.isTariffChangeDataReady() && !handled) {
          handled = true;
          this.openTariffChangeSelectionPopup();
        }
      },
      { injector: this.injector },
    );
  }

  private isTariffChangeDataReady(): boolean {
    return this.store.associations() !== undefined && this.loadingService.hasFinished(this.addressProductsLoadState());
  }

  // Tariff Change Selection Popup - END

  /*
  Order Products Popup - START
  - footer-link "Produkte bestellen" should open OrderProductsPopupComponent as Popup
  - problem: if you switch portal (e.g. from /privat to /business) you have to load a new app
  -- so we have to add custom query param to switch-portal-url
  -- and we have to check on app-load in new portal if popup has to be displayed
  */
  orderProducts(type: EnvironmentCustomerType) {
    if (type !== this.environmentService.getPortalCustomerType()) {
      this.navigationLogic.switchPortal(`?${this.orderProductsQueryParamName}=true`);
      return;
    }
    this.openProductOrderPopup();
  }

  private orderProductsQueryParamName = 'produkteBestellenPopup';

  private openProductOrderPopup(): void {
    const model: IPopupModelV2 = {
      id: 'products-footer-order-products',
      component: OrderProductsPopupComponent,
      componentData: this.environmentService.getPortalCustomerType() === 'privat',
      dismissable: true,
      titleKey: 'newClient.title',
      modalSize: 'xl',
    };
    this.popupService.openV2(model);
  }

  private checkOrderProductPopup() {
    const canOpen = location.search.indexOf(this.orderProductsQueryParamName) !== -1;

    if (canOpen) {
      this.openProductOrderPopup();
      const url = new URL(location.href);
      url.searchParams.delete(this.orderProductsQueryParamName);
      history.replaceState(null, null, url);
    }
  }

  // Order Products Popup - END
}
